import { SearchAdapter } from '@empathy/search-adapter';
import { StorageService } from '@empathy/storage-service';
import { deepMerge } from '@empathybroker/deep-merge';
import { inject, injectable, interfaces, named } from 'inversify';
import Vue, { VNodeData, VueConstructor } from 'vue';
import { Store, StoreOptions } from 'vuex';
import { ClientDynamicConfig } from '../api/api.interfaces';
import { BUS } from '../bus';
import { EVENTS } from '../bus/events.const';
import Core from '../components/Core.vue';
import {
  DEFAULT_DEVICES_CONFIGURATION,
  DEFAULT_DEVICES_DETECTION_CONFIG,
  DEFAULT_DYNAMIC_CONFIG,
  DEFAULT_PHANTOM_COMPONENTS_CONFIG
} from '../config';
import { container } from '../container';
import { DEPENDENCIES, STORAGE } from '../container/dependencies.const';
import { FILTERS_REGISTRY } from '../filters';
import { logger } from '../logger';
import {
  ConfigState,
  ConfigurationsByDevice,
  DeviceDetectionStrategy,
  DevicesDetectionConfig,
  DynamicConfig,
  EmpathyXConfig,
  PhantomComponent,
  PhantomComponentsConfig
} from '../models';
import { EmpathyXPlugin } from '../plugins';
import { DeviceDetector } from '../services';
import { RootStoreState } from '../store';
import { createStore } from '../store/store';
import { DeepPartial } from '../utils';
import { Wire, WIRING } from '../wiring';
import { AdapterFactoryOptions, ConfiguratorOptions } from './builders.interfaces';
import { ConfigBuilder } from './config.builder';

@injectable()
export class AppBuilder {
  protected adapter: SearchAdapter;
  protected adapterFactory: (factoryOptions: AdapterFactoryOptions) => SearchAdapter;
  protected appComponent: VueConstructor = Core;
  protected appSelector = '#empathy-x-placeholder';
  protected baseDevice = 'mobile';
  protected buildTimestamp: number;
  protected configBuilders: { [deviceName: string]: ConfigBuilder } = {};
  protected configurationsByDevice: ConfigurationsByDevice = {};
  protected customConfigurators: ((configuratorOptions: ConfiguratorOptions) => void)[] = [];
  protected detectedDevice = this.baseDevice;
  protected deviceDetectionConfig: DevicesDetectionConfig = DEFAULT_DEVICES_DETECTION_CONFIG;
  protected deviceDetectionStrategy: DeviceDetectionStrategy = 'screen-size';
  protected dynamicConfig: DynamicConfig = DEFAULT_DYNAMIC_CONFIG;
  protected phantomComponentsConfig: PhantomComponentsConfig = DEFAULT_PHANTOM_COMPONENTS_CONFIG;
  protected portals: { [domSelector: string]: VueConstructor } = {};
  protected selectedConfig: EmpathyXConfig;
  protected store: Store<RootStoreState>;
  protected storeOptions: StoreOptions<RootStoreState>;

  constructor(
    @inject(DEPENDENCIES.ConfigBuilder) protected readonly configBuilderConstructor: interfaces.Newable<ConfigBuilder>,
    @inject(DEPENDENCIES.StorageService) @named(STORAGE.Local) protected readonly storageService: StorageService
  ) {}

  build(dynamicConfig: ClientDynamicConfig): Vue {
    try {
      logger.info(`Running EmpathyX v${ EMPATHY_X_VERSION }`);
      this.dynamicConfig = deepMerge(this.dynamicConfig, this.addOptionalParameters(dynamicConfig));
      this.buildBaseMergedConfig();
      this.buildNoBaseMergedConfig();
      this.applyConfig();
      this.createAdapter();
      this.checkIfIsNewVersion();
      return this.instantiateEmpathyX();
    } catch (e) {
      logger.error('[ConfigBuilder]', e);
    }
  }

  protected addOptionalParameters(dynamicConfig: ClientDynamicConfig): ClientDynamicConfig {
    dynamicConfig.searchLang = dynamicConfig.searchLang || dynamicConfig.lang;
    return dynamicConfig;
  }

  protected buildBaseMergedConfig(): EmpathyXConfig {
    return this.mergeConfig(this.baseDevice, this.configBuilders[this.baseDevice]);
  }

  protected buildNoBaseMergedConfig(): EmpathyXConfig[] {
    return Object.entries<ConfigBuilder>(this.configBuilders)
      .filter(([deviceName]) => deviceName !== this.baseDevice)
      .map(([deviceName, configBuilder]) => this.mergeConfig(deviceName, configBuilder));
  }

  protected mergeConfig(deviceName: string, configBuilder: ConfigBuilder): EmpathyXConfig {
    const partialConfig = configBuilder.build();
    return this.configurationsByDevice[deviceName] =
      deepMerge({}, this.configurationsByDevice[this.baseDevice], DEFAULT_DEVICES_CONFIGURATION[deviceName], partialConfig);
  }

  addSubConfig(subConfigurator: (builder: AppBuilder) => void): AppBuilder {
    subConfigurator(this);
    return this;
  }

  overrideFilters(filters: DeepPartial<typeof FILTERS_REGISTRY>): AppBuilder {
    Object.assign(FILTERS_REGISTRY, filters);
    return this;
  }

  overrideStore(storeOptions: StoreOptions<RootStoreState>): AppBuilder {
    this.storeOptions = storeOptions;
    return this;
  }

  removeConfigFor(name: string): AppBuilder {
    delete this.configBuilders[name];
    return this;
  }

  setAppComponent(appComponent: VueConstructor): AppBuilder {
    this.appComponent = appComponent;
    return this;
  }

  setAppElementSelector(selector: string): AppBuilder {
    this.appSelector = selector;
    return this;
  }

  setDefaultDevice(defaultDevice: string): AppBuilder {
    this.baseDevice = defaultDevice;
    return this;
  }

  setConfigFor(deviceName: string, configurator?: (configBuilder: ConfigBuilder) => void): AppBuilder {
    if (!this.configBuilders[deviceName]) {
      this.configBuilders[deviceName] = new this.configBuilderConstructor();
    }
    if (configurator) {
      configurator(this.configBuilders[deviceName]);
    }
    return this;
  }

  setCustomConfigurator(configurator: (configuratorOptions: ConfiguratorOptions) => void): AppBuilder {
    this.customConfigurators.push(configurator);
    return this;
  }

  setDevicesDetectionConfig(deviceDetectionConfig: DeepPartial<DevicesDetectionConfig>): AppBuilder {
    this.deviceDetectionConfig = deepMerge({}, DEFAULT_DEVICES_DETECTION_CONFIG, deviceDetectionConfig);
    return this;
  }

  setDeviceDetectionStrategy(deviceDetectionStrategy: DeviceDetectionStrategy): AppBuilder {
    this.deviceDetectionStrategy = deviceDetectionStrategy;
    return this;
  }

  setDynamicConfig(dynamicConfig: DeepPartial<DynamicConfig>): AppBuilder {
    this.dynamicConfig = deepMerge({}, DEFAULT_DYNAMIC_CONFIG, dynamicConfig);
    return this;
  }

  setPhantomComponentsConfig(phantomComponentsConfig: DeepPartial<PhantomComponentsConfig>): AppBuilder {
    this.phantomComponentsConfig = deepMerge({}, DEFAULT_PHANTOM_COMPONENTS_CONFIG, phantomComponentsConfig);
    return this;
  }

  addWireToEventBeforeWire(newWire: Wire, event: string, existingWire: Wire): AppBuilder {
    const wires: Wire[] = WIRING[event];
    if (wires) {
      const wirePosition = wires.indexOf(existingWire);
      if (wirePosition > -1) {
        wires.splice(wirePosition, 0, newWire);
      }
    }
    return this;
  }

  addWireToEventAfterWire(newWire: Wire, event: string, existingWire: Wire): AppBuilder {
    const wires: Wire[] = WIRING[event];
    if (wires) {
      const wirePosition = wires.indexOf(existingWire);
      if (wirePosition > -1) {
        wires.splice(wirePosition + 1, 0, newWire);
      }
    }
    return this;
  }

  addWireToEvent(wire: Wire, event: string) {
    const wires: Wire[] = WIRING[event];
    if (wires) {
      wires.push(wire);
    } else {
      WIRING[event] = [wire];
    }
    return this;
  }

  replaceWireInEvent(newWire: Wire, event: string, existingWire: Wire): AppBuilder {
    const wires: Wire[] = WIRING[event];
    if (wires) {
      const wirePosition = wires.indexOf(existingWire);
      if (wirePosition > -1) {
        wires.splice(wirePosition, 1, newWire);
      }
    }
    return this;
  }

  replaceWireInAllEvents(newWire: Wire, existingWire: Wire): AppBuilder {
    Object.keys(WIRING).forEach((event: string) => this.replaceWireInEvent(newWire, event, existingWire));
    return this;
  }

  replaceAllWiresInEvent(wires: Wire[], event: string): AppBuilder {
    WIRING[event] = wires;
    return this;
  }

  removeWireFromEvent(wire: Wire, event: string): AppBuilder {
    const wires: Wire[] = WIRING[event];
    if (wires) {
      const wirePosition = wires.indexOf(wire);
      if (wirePosition > -1) {
        wires.splice(wirePosition, 1);
      }
    }
    return this;
  }

  removeWireFromAllEvents(wire: Wire): AppBuilder {
    Object.keys(WIRING).forEach((event: string) => this.removeWireFromEvent(wire, event));
    return this;
  }

  setAdapter(adapter: SearchAdapter): AppBuilder {
    this.adapter = adapter;
    return this;
  }

  setAdapterFactory(adapterFactory: (factoryOptions: AdapterFactoryOptions) => SearchAdapter): AppBuilder {
    this.adapterFactory = adapterFactory;
    return this;
  }

  setBuildTimestamp(newBuildTimestamp: number): AppBuilder {
    this.buildTimestamp = newBuildTimestamp;
    return this;
  }

  protected createAdapter(): void {
    if (this.adapterFactory) {
      this.adapter = this.adapterFactory({ store: this.store, container });
    }
    container.bind<SearchAdapter>(DEPENDENCIES.Adapter).toConstantValue(this.adapter);
  }

  protected detectDevice(): string {
    if (this.deviceDetectionStrategy === 'scope') {
      return this.dynamicConfig.scope;
    } else if (this.deviceDetectionStrategy === 'screen-size') {
      return this.runScreenWidthDetector();
    } else {
      return this.baseDevice;
    }
  }

  protected runScreenWidthDetector(): string {
    const screenWidthDetector = container.get<DeviceDetector>(DEPENDENCIES.ScreenWidthDetector);
    return screenWidthDetector.detect(this.deviceDetectionConfig);
  }

  protected applyConfig() {
    this.detectedDevice = this.detectDevice();
    this.selectedConfig = this.configurationsByDevice[this.detectedDevice] as EmpathyXConfig;
    this.configureStore();
    this.installEmpathyXVuePlugin();
    this.runCustomConfigurator();
  }

  protected configureStore(): void {
    const configState = this.createConfigState();
    this.store = createStore(configState, this.storeOptions) as Store<RootStoreState>;
    container.bind<Store<RootStoreState>>(DEPENDENCIES.Store).toConstantValue(this.store);
  }

  protected installEmpathyXVuePlugin(): void {
    Vue.use(new EmpathyXPlugin());
  }

  protected createConfigState(): ConfigState {
    return {
      baseDevice: this.baseDevice,
      config: this.configurationsByDevice,
      device: this.detectedDevice,
      devicesDetectionConfig: this.deviceDetectionConfig,
      deviceDetectionStrategy: this.deviceDetectionStrategy,
      dynamic: this.dynamicConfig
    };
  }

  protected runCustomConfigurator() {
    this.customConfigurators.forEach(configurator => configurator({ container }));
  }

  addPortal(component: VueConstructor, domSelector: string): AppBuilder {
    this.portals[domSelector] = component;
    return this;
  }

  protected createDomElementIfNeeded(selector: string) {
    if (selector && !document.querySelector(selector)) {
      const element = document.createElement('div');

      if (selector.startsWith('.')) {
        element.className = selector.substr(1);
      } else if (selector.startsWith('#')) {
        element.id = selector.substr(1);
      }

      document.body.appendChild(element);
    }
  }

  protected instantiateEmpathyX() {
    const empathyX = this.instantiateVue();
    this.instantiatePortals();
    this.instantiatePhantoms();
    return empathyX;
  }

  protected createVueInstance(domSelector: string, component: string | VueConstructor, nodeData?: VNodeData): Vue {
    return new Vue({
      el: domSelector,
      store: this.store,
      render(h) {
        return h(component, nodeData);
      }
    });
  }

  protected instantiateVue(): Vue {
    this.createDomElementIfNeeded(this.appSelector);
    return this.createVueInstance(this.appSelector, this.appComponent);
  }

  protected instantiatePortals() {
    Object.entries<VueConstructor>(this.portals).forEach(([domSelector, component]) => {
      this.createDomElementIfNeeded(domSelector);
      this.createVueInstance(domSelector, component, { staticClass: 'ebx-portal' });
    });
  }

  protected instantiatePhantoms() {
    Object.entries<PhantomComponent>(this.phantomComponentsConfig.components)
      .filter(([, phantomComponent]) => phantomComponent)
      .forEach(([selector, phantomComponent]) => {
        const domSelector = `.ebx-phantom-${ selector }`;
        this.createDomElementIfNeeded(domSelector);
        this.createVueInstance(domSelector, phantomComponent.component, phantomComponent.data);
      });
  }

  protected checkIfIsNewVersion() {
    const oldTimestamp = this.storageService.getItem('build-timestamp');
    if (this.buildTimestamp && this.buildTimestamp !== oldTimestamp) {
      this.storageService.setItem('build-timestamp', this.buildTimestamp);
      BUS.emit(EVENTS.NewVersionDownloaded, this.buildTimestamp);
    }
  }
}
